import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { MinStockWithDetails, Stone, Location } from '../types';
import { SkeletonTable } from './Skeleton';
import { groupStonesByBaseName, getSizeLabel } from '../utils/stoneMatrixHelper';

// Funciones de utilidad para colores y alertas
const getStockColor = (stock: number, minStock: number): string => {
  if (minStock === 0) return 'var(--text-primary)'; // Si no hay mínimo configurado, color normal
  if (stock < minStock) {
    return 'var(--error)'; // Rojo: por debajo del mínimo
  } else if (stock <= minStock * 1.2) {
    return 'var(--warning)'; // Amarillo: igual o hasta 20% por encima del mínimo
  } else {
    return 'var(--success)'; // Verde: más de 20% por encima del mínimo
  }
};

const shouldShowAlert = (stock: number, minStock: number): boolean => {
  if (minStock === 0) return false;
  return stock < minStock * 0.5; // Alerta si está por debajo del 50% del mínimo
};

export default function MinStockStones() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [minStock, setMinStock] = useState<MinStockWithDetails[]>([]);
  const [stockWithMinStock, setStockWithMinStock] = useState<Array<{ item_id: number; current_stock: number }>>([]);
  const [stones, setStones] = useState<Stone[]>([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [minStockRes, stonesRes, stockRes, locationsRes] = await Promise.all([
        api.get('/min-stock'),
        api.get('/stones'),
        api.get('/stock', { params: { item_type: 'STONE' } }),
        api.get('/locations'),
      ]);

      // Obtener la ubicación ALMACEN_PIEDRAS
      const almacenPiedras = locationsRes.data.find((loc: Location) => loc.code === 'ALMACEN_PIEDRAS');
      if (!almacenPiedras) {
        showError('No se encontró la ubicación ALMACEN_PIEDRAS');
        return;
      }

      // Filtrar solo piedras
      const stoneMinStock = minStockRes.data.filter((ms: MinStockWithDetails) => ms.item_type === 'STONE' && ms.location_id === null);
      setMinStock(stoneMinStock);
      setStones(stonesRes.data);

      // Procesar stock para mostrar solo piedras de ALMACEN_PIEDRAS
      const stockData = stockRes.data.filter((stock: any) => 
        stock.item_type === 'STONE' && stock.location_id === almacenPiedras.id
      );
      
      const minStockMap = new Map<string, MinStockWithDetails>();
      stoneMinStock.forEach((ms: MinStockWithDetails) => {
        const key = `${ms.item_type}-${ms.item_id}-null`;
        minStockMap.set(key, ms);
      });

      // Crear un mapa de stock por stone_id para acceso rápido
      const stockMap = new Map<number, number>();
      stockData.forEach((stock: any) => {
        stockMap.set(stock.item_id, stock.qty);
      });

      // Crear array con stock para la vista matricial
      const stockWithMin = stonesRes.data
        .filter((stone: Stone) => stone.is_active)
        .map((stone: Stone) => ({
          item_id: stone.id,
          current_stock: stockMap.get(stone.id) || 0,
        }));

      setStockWithMinStock(stockWithMin);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };


  const handleRecalculateStoneMinStocks = async () => {
    try {
      setRecalculating(true);
      
      const response = await api.get('/stone-consumptions/recalculate-min-stocks');
      const result = response.data;
      
      const skipped = result.skipped || 0;
      const total = result.total || 0;
      const updated = result.updated || 0;
      const created = result.created || 0;
      const alreadyCorrect = result.alreadyCorrect || 0;
      const processed = updated + created;
      
      if (processed === 0) {
        // No se procesó ninguna piedra
        if (skipped === total) {
          // Todas las piedras fueron omitidas (sin consumos)
          showError(`No se pudieron calcular mínimos. ${skipped} piedras omitidas (sin consumos registrados o con consumo promedio de 0). Total piedras: ${total}`);
        } else if (alreadyCorrect > 0) {
          // Algunas piedras tienen consumos pero ya tienen mínimos correctos
          showSuccess(`Recálculo completado: ${alreadyCorrect} piedras con mínimos ya correctos, ${skipped} omitidas (sin consumos). Total: ${total}`);
        } else {
          // Caso inesperado
          showError(`No se pudieron calcular mínimos. ${skipped} piedras omitidas. Total: ${total}`);
        }
      } else {
        // Se procesaron algunas piedras
        const parts = [];
        if (updated > 0) parts.push(`${updated} actualizados`);
        if (created > 0) parts.push(`${created} creados`);
        if (alreadyCorrect > 0) parts.push(`${alreadyCorrect} ya correctos`);
        if (skipped > 0) parts.push(`${skipped} omitidas (sin consumos)`);
        
        showSuccess(`Mínimos recalculados: ${parts.join(', ')}. Total: ${total}`);
      }
      await loadData();
    } catch (err: any) {
      console.error('Error al recalcular mínimos:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Error al recalcular mínimos de piedras';
      showError(errorMessage);
    } finally {
      setRecalculating(false);
    }
  };


  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="section-header" style={{ background: '#ffffff' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: 'var(--text-primary)' }}>Establecer mínimos - Piedras</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: 0 }}>
            Visualización de mínimos de stock para piedras agrupadas por tipo y tamaño. Los mínimos se calculan automáticamente basándose en consumos históricos.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        {canEdit('min-stock-stones') && (
          <button 
            className="btn btn-primary" 
            onClick={handleRecalculateStoneMinStocks}
            disabled={recalculating}
            title="Recalcula los mínimos de piedras basándose en consumos históricos (usa las últimas 8 semanas de datos)"
          >
            {recalculating ? 'Recalculando...' : '🔄 Recalcular Mínimos Automáticamente'}
          </button>
        )}
      </div>

      {/* Vista Matricial */}
      {(
        <>
          <div style={{ marginBottom: '2rem' }}>
            {(() => {
              // Crear un mapa de mínimos por stone_id
              const minStockMap = new Map<number, { min_qty_base: number }>();
              minStock.forEach(ms => {
                minStockMap.set(ms.item_id, {
                  min_qty_base: ms.min_qty_base,
                });
              });

              // Crear un mapa de stock por stone_id
              const stockMap = new Map<number, number>();
              stockWithMinStock.forEach(s => {
                stockMap.set(s.item_id, s.current_stock);
              });

              // Agrupar piedras
              const stonesWithData = stones.filter(s => s.is_active).map(stone => ({
                ...stone,
                min_qty_base: minStockMap.get(stone.id)?.min_qty_base || 0,
                current_stock: stockMap.get(stone.id) || 0,
              }));

              const { groups, allSizes } = groupStonesByBaseName(stonesWithData);

              if (groups.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    No hay piedras con mínimos configurados
                  </div>
                );
              }

              return (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="sticky-col">Código</th>
                        <th className="sticky-col">Nombre</th>
                        {allSizes.map((size) => (
                          <th key={size} style={{ textAlign: 'center' }}>
                            {getSizeLabel(size)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((group) => (
                        <tr key={`${group.cod_piedra}-${group.nombre_piedra}`}>
                          <td className="sticky-col">
                            <strong>{group.cod_piedra}</strong>
                          </td>
                          <td className="sticky-col">
                            <strong>{group.nombre_piedra}</strong>
                          </td>
                          {allSizes.map((size) => {
                            const stonesForSize = group.sizes.get(size) || [];
                            if (stonesForSize.length === 0) {
                              return (
                                <td key={size} style={{ textAlign: 'center', padding: '0.5rem' }}>
                                </td>
                              );
                            }
                            const stone = stonesForSize[0] as any;
                            const minQty = stone.min_qty_base || 0;
                            const currentStock = stone.current_stock || 0;
                            
                            return (
                              <td key={size} style={{ textAlign: 'center', padding: '0.5rem' }}>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    position: 'relative',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    justifyContent: 'center',
                                    minWidth: '80px',
                                  }}
                                >
                                  <strong style={{ fontSize: '1.125rem', color: getStockColor(currentStock, minQty) }}>
                                    {currentStock}
                                  </strong>
                                  {shouldShowAlert(currentStock, minQty) && (
                                    <span style={{ marginLeft: '0.25rem', fontSize: '1rem' }} title="Stock crítico: menos del 50% del mínimo">
                                      ⚠️
                                    </span>
                                  )}
                                  {minQty > 0 && (
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
                                      ({minQty})
                                    </span>
                                  )}
                                  {currentStock === 0 && minQty === 0 && (
                                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginLeft: '0.25rem' }}>
                                      (s/m)
                                    </span>
                                  )}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

